import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { AuthContext } from './AuthContext';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [accessToken, setAccessToken] = useState<string | null>(localStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState<string | null>(localStorage.getItem('refreshToken'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!localStorage.getItem('accessToken'));

  // Define refreshAccessToken function before using it in useEffect
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      if (!refreshToken) {
        console.error('No refresh token available');
        return false;
      }

      // Get API base URL from environment variables
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      
      // Construct refresh URL by removing /api/v1 suffix if present
      const baseUrl = apiBaseUrl ? apiBaseUrl.replace(/\/api\/v1$/, '') : 'http://localhost:8080';
      const refreshUrl = `${baseUrl}/auth/refresh`;
      console.log('Using refresh URL:', refreshUrl);
      
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        console.error('Token refresh failed:', response.status);
        return false;
      }

      const data = await response.json();
      
      // Store new tokens
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setIsAuthenticated(true);
      
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }, [refreshToken]);

  // Check if tokens exist on mount and set up event listeners
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    
    if (storedAccessToken && storedRefreshToken) {
      setAccessToken(storedAccessToken);
      setRefreshToken(storedRefreshToken);
      setIsAuthenticated(true);
    }
    
    // Set up event listener for token refresh requests from api.ts
    const handleRefreshRequest = async () => {
      console.log('Received token refresh request from API');
      const success = await refreshAccessToken();
      
      // Dispatch the result back
      const resultEvent = new CustomEvent('auth:refreshTokenResult', {
        detail: { success }
      });
      window.dispatchEvent(resultEvent);
    };
    
    window.addEventListener('auth:refreshToken', handleRefreshRequest);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('auth:refreshToken', handleRefreshRequest);
    };
  }, [refreshAccessToken]); // Add refreshAccessToken to the dependency array

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('Login attempt with:', username);
      
      // Get API base URL from environment variables
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      console.log('API Base URL from env:', apiBaseUrl);
      
      const loginUrl = `${apiBaseUrl}/auth/login`;
      console.log('Using login URL:', loginUrl);
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      console.log('Login response status:', response.status);
      
      // Parse the response body
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Login failed:', data);
        return { 
          success: false, 
          error: data.error || `Login failed with status ${response.status}` 
        };
      }

      console.log('Login successful, storing tokens');
      
      // Store tokens
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
  }, []);

  const getAccessToken = useCallback(() => {
    return accessToken;
  }, [accessToken]);

  // refreshAccessToken function is now defined above

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        refreshToken,
        login,
        logout,
        getAccessToken,
        refreshAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
